import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { replanDemo } from '@/lib/replanDemo';
import { at, getCarrier } from '@/data/arrivalsData';
import { GATE_CAPACITY } from '@/data/siteStats';
import { t, getLanguage } from '@/i18n';
import { ArrowRightLeft, Check, Loader2, Phone, Truck, Wand2, X } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

export const REPLAN_STATES = ['no-info', 'not-coming', 'no-power', 'rescheduled'];
const STEP_ICONS = { move: Truck, swap: ArrowRightLeft, contact: Phone, keep: Check };

const pad = (n) => String(n).padStart(2, '0');
const hhmm = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const brief = (l) => ({
  id: l.id, plate: l.plate, carrier: getCarrier(l.carrierId)?.name || '—', gate: l.gate, state: l.state,
  plannedAt: hhmm(l.plannedAt), eta: l.eta ? hhmm(l.eta) : null,
});

// "Propose a solution" for a loading whose truck will not come on time (#68).
export const ReplanProposal = ({ loading, loadings, onAccept }) => {
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null); // { mode, result, status: 'open' | 'accepted' | 'rejected' }

  const propose = async () => {
    setBusy(true);
    const input = {
      lang: getLanguage(), now: hhmm(new Date().toISOString()), capacity: GATE_CAPACITY,
      problem: brief(loading), day: loadings.filter(l => l.id !== loading.id && l.state !== 'loaded').slice(0, 60).map(brief),
    };
    const { mode, result } = await runAi('replan', input, replanDemo);
    setPlan({ mode, result, status: 'open' });
    setBusy(false);
  };

  const accept = () => {
    onAccept(loading.id, { eta: at(plan.result.newTime, plan.result.dayOffset), summary: plan.result.summary });
    setPlan({ ...plan, status: 'accepted' });
  };

  if (!plan) {
    return (
      <Button variant="outline" size="sm" className="gap-1 w-full" onClick={propose} disabled={busy}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} {t('ai.replan.propose')}
      </Button>
    );
  }
  const r = plan.result;
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" aria-live="polite">
      <div className="flex items-center gap-2 text-xs font-semibold text-primary">
        <Wand2 className="h-3.5 w-3.5" /> {t('ai.replan.title')} <HelpTip id="replan" />
        <AiBadge mode={plan.mode} className="ml-auto" />
      </div>
      <p className="text-sm font-medium">{r.summary}</p>
      <ol className="space-y-1.5">
        {r.steps.map((s, i) => {
          const Icon = STEP_ICONS[s.action] || Check;
          return (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Icon className="h-4 w-4 mt-0.5 shrink-0 text-primary" /> {s.detail}
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground">{t('ai.replan.risk')}: {r.risk}</p>
      {plan.status === 'open' && (
        <div className="flex gap-2">
          <Button size="sm" className="gap-1" onClick={accept}><Check className="h-3.5 w-3.5" /> {t('ai.replan.accept')}</Button>
          <Button size="sm" variant="ghost" className="gap-1" onClick={() => setPlan({ ...plan, status: 'rejected' })}>
            <X className="h-3.5 w-3.5" /> {t('ai.replan.reject')}
          </Button>
        </div>
      )}
      {plan.status !== 'open' && (
        <div className="text-xs text-muted-foreground">{t(plan.status === 'accepted' ? 'ai.replan.accepted' : 'ai.replan.rejected')}</div>
      )}
    </div>
  );
};
